import { Component, effect, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { User } from '@app/_models';
import { SignalRService } from '@app/_services/signal-r.service';
import { AccountService } from '@app/_services/account.service';

interface Activity {
  type: string;
  message: string;
  time: string;
  timestamp: Date;         // ← raw date for recalculation
}

@Component({
  selector: 'app-admin-dashboard',
  templateUrl: './admin-dashboard.component.html',
  styleUrls: ['./admin-dashboard.component.scss'],
  standalone: true,
  imports: [CommonModule, RouterModule, FormsModule]
})
export class AdminDashboardComponent implements OnInit {
  @Input() user: User;
  adminNotification: any;
  activeDealerThisMonthCounter = 0;
  todaysCertificateCounter = 0;
  pendingRequestCounter = 0;
  
  statistics = [
    { title: 'Active Dealers This Month', value: this.activeDealerThisMonthCounter, icon: 'fa-briefcase', color: '#4CAF50', loading: true },
    { title: 'Certificates Issued Today', value: this.todaysCertificateCounter, icon: 'fa-certificate', color: '#2196F3', loading: true },
    { title: 'Pending Approvals', value: this.pendingRequestCounter, icon: 'fa-clock', color: '#F44336', loading: true }
  ];
  
  quickLinks = [
    { title: 'Issue Certificate', icon: 'fa-plus-square', route: '/issueCertificate', color: '#4CAF50' },
    { title: 'Print Certificate', icon: 'fa-print', route: '/reports/printCertificate', color: '#2196F3' },
    { title: 'Dealer Management', icon: 'fa-users', route: '/users', color: '#FF9800' },
    { title: 'Payment Report', icon: 'fa-credit-card', route: '/reports/transactionReport', color: '#9C27B0' }
  ];
  
  recentActivities: Activity[] = [];
  activityFilter = '';

  get filteredActivities(): Activity[] {
    if (!this.activityFilter) return this.recentActivities;
    return this.recentActivities.filter(a => a.type === this.activityFilter);
  }

  constructor(private signalRService: SignalRService, private accountService: AccountService) {
    this.adminNotification = this.signalRService.adminNotification;

    effect(() => {
      // …existing code that updates your three counters…
      this.activeDealerThisMonthCounter = this.adminNotification().activeDealerThisMonthCount;
      this.todaysCertificateCounter       = this.adminNotification().todaysCertificateCounter;
      this.pendingRequestCounter          = this.adminNotification().pendingRequestCounter;

      // ← NEW: push updated values into the stats array
      this.statistics[0].value   = this.activeDealerThisMonthCounter;
      this.statistics[1].value   = this.todaysCertificateCounter;
      this.statistics[2].value   = this.pendingRequestCounter;

      // ← NEW: turn off loading on first real data push
      this.statistics.forEach(s => (s.loading = false));

      // …existing code that adds a new Activity to recentActivities…
      const notif = this.adminNotification();
      if (notif) {
        const newAct: Activity = {
          type: notif.notificationType,
          message: notif.notificationMessage,
          timestamp: new Date(notif.receivedDateTime),               // ← store raw date
          time: this.getTimeAgo(notif.receivedDateTime)              // ← initial string
        };
        this.recentActivities.unshift(newAct);
        if (this.recentActivities.length > 500) {
          this.recentActivities.pop();
        }

        // ← Recompute “time ago” for all entries
        this.recentActivities.forEach(a =>
          a.time = this.getTimeAgo(a.timestamp)
        );
      }
    });
  }

  ngOnInit(): void {
    this.accountService.getTodaysCache().subscribe({
      next: (data:any) => {
        if (!data) return;
        if (data.lastTenNotifications && data.lastTenNotifications.length > 0) {
          data.lastTenNotifications.forEach((notif: any) => {
            const dt = notif.receivedDateTime ? new Date(notif.receivedDateTime) : new Date();
            const newAct: Activity = {
              type: notif.notificationType,
              message: notif.notificationMessage,
              timestamp: dt,                                          // ← store raw date
              time: this.getTimeAgo(dt)                               // ← initial string
            };
            this.recentActivities.unshift(newAct);
          });
          this.activeDealerThisMonthCounter = data.activeDealerThisMonth.length;
          this.todaysCertificateCounter = data.todaysCertificateCounter;
          this.pendingRequestCounter = data.pendingRequestCounter;
          this.statistics[0].value   = this.activeDealerThisMonthCounter;
          this.statistics[1].value   = this.todaysCertificateCounter;
          this.statistics[2].value   = this.pendingRequestCounter;
            }
      },
      error: (error) => {
        console.error('Error fetching cache data:', error);
      }
    });
    // removed static setTimeout loading simulation—
    // stats now update dynamically via the effect above.
  }

  private getTimeAgo(date: Date): string {
    const now    = Date.now();
    const then   = new Date(date).getTime();
    const mins   = Math.floor((now - then) / 60000);
    if (mins < 60) return `${mins} minutes ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs} hours ago`;
    return `${Math.floor(hrs/24)} days ago`;
  }
}
